import { useCallback, useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { TraceBoardItem } from '../interfaces/TraceBoardItem';

export type RegisterItemElementFunction = (
  rowIndex: number,
  columnIndex: number,
  item: TraceBoardItem,
  element: HTMLElement
) => void;

export interface TraceBoardItemElement {
  rowIndex: number;
  columnIndex: number;
  item: TraceBoardItem;
  element: HTMLElement;
}

interface SubscriptionCallback {
  (rowIndex: number, columnIndex: number): void;
}

interface ItemElementSubscription {
  rowIndex: number;
  columnIndex: number;
  callback: SubscriptionCallback;
}

export function useItemElementSubscription() {
  const [columnItemElements, setColumnItemElements] = useState<
    TraceBoardItemElement[]
  >([]);
  const columnItemElementsRef = useRef(columnItemElements);
  const subscriptionRef = useRef<ItemElementSubscription[]>([]);
  const observerRef = useRef(
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        columnItemElementsRef.current.forEach(
          ({ rowIndex, columnIndex, element }) => {
            if (element === entry.target) {
              publishItemElementChange(rowIndex, columnIndex);
            }
          }
        );
      });
    })
  );
  useEffect(() => void (columnItemElementsRef.current = columnItemElements), [
    columnItemElements
  ]);

  /**
   * Assigns the DOM element for the item card for use with rendering
   * the line connectors
   */
  const registerItemElement = useCallback(
    (
      rowIndex: number,
      columnIndex: number,
      item: TraceBoardItem,
      element: HTMLElement
    ) => {
      setColumnItemElements(columnItemElements => {
        // make sure only one reference to a column item
        const columnItemElement = columnItemElements.find(
          c =>
            c.rowIndex === rowIndex &&
            c.columnIndex === columnIndex &&
            c.item.id === item.id
        );
        if (columnItemElement) {
          observerRef.current.unobserve(columnItemElement.element);
          columnItemElement.element = element;
        } else {
          columnItemElements.push({ rowIndex, columnIndex, item, element });
        }
        observerRef.current.observe(element);
        return [...columnItemElements];
      });
    },
    []
  );

  const unregisterItemElement = useCallback(
    (rowIndex: number, columnIndex: number, item: TraceBoardItem) => {
      setColumnItemElements(columnItemElements => {
        const index = columnItemElements.findIndex(
          c =>
            c.rowIndex === rowIndex &&
            c.columnIndex === columnIndex &&
            c.item.id === item.id
        );
        if (index > -1) {
          const [{ element }] = columnItemElements.splice(index, 1);
          observerRef.current.unobserve(element);
        }
        return [...columnItemElements];
      });
    },
    []
  );

  const subscribeItemElementChange = useCallback(
    (rowIndex: number, columnIndex: number, callback: SubscriptionCallback) => {
      subscriptionRef.current.push({ rowIndex, columnIndex, callback });
    },
    []
  );

  const unsubscribeItemElementChange = useCallback(
    (rowIndex: number, columnIndex: number, callback: SubscriptionCallback) => {
      const index = subscriptionRef.current.findIndex(
        s =>
          s.rowIndex === rowIndex &&
          s.columnIndex === columnIndex &&
          s.callback === callback
      );
      if (index > -1) {
        subscriptionRef.current.splice(index, 1);
      }
    },
    []
  );

  /**
   * Notify all subscriptions in current row for column affected as well as
   * column to left and column to right
   */
  const publishItemElementChange = useCallback(
    (rowIndex: number, columnIndex: number) => {
      for (const subscription of subscriptionRef.current) {
        if (
          subscription.rowIndex === rowIndex &&
          [columnIndex, columnIndex - 1, columnIndex + 1].includes(
            subscription.columnIndex
          )
        ) {
          subscription.callback(rowIndex, columnIndex);
        }
      }
    },
    []
  );

  return {
    columnItemElements,
    registerItemElement,
    unregisterItemElement,
    subscribeItemElementChange,
    unsubscribeItemElementChange,
    publishItemElementChange
  };
}
